/**
 * What the signed-in user may do, for the browser — disable (never hide) what
 * the server would refuse. The server's level table
 * (`src/server/support-levels.ts`) is the authority; this only mirrors it so a
 * VELOX support rep sees a greyed control and a chip instead of an error.
 * Plan: docs/plans/2026-09-25-ruby-hq-support-role.md §7.
 */
import { useAuth } from './useAuth';

export type AccessLevel = 0 | 1 | 2 | 3;

export interface Access {
  level: AccessLevel;
  /** Ruby admin — the only level for anything Ruby-only. */
  isRubyAdmin: boolean;
  /** Ruby admin or VELOX support: may open the admin pages. */
  isStaff: boolean;
  /** May change a control held at `min` (1 = VELOX L1, 2 = VELOX L2, 3 = Ruby only). */
  canEdit: (min: 1 | 2 | 3) => boolean;
  /** A clinic user manager: may open User Management for their clinic's staff. */
  isUserManager: boolean;
}

/**
 * A clinic user whose subrole grants `users.manage`. `/api/auth/me` lists it
 * for clinic staff only when the manager check passes (Require 2FA, every
 * location), so the page works whenever this is true. The server's
 * `admin-api.ts` is the boundary. Plan: docs/plans/2026-10-22-clinic-user-managers.md
 */
export function isUserManager(user: { role?: string; permissions?: string[] } | null | undefined): boolean {
  return user?.role === 'client' && (user.permissions ?? []).includes('users.manage');
}

export function levelOf(role: string | undefined, level?: AccessLevel): AccessLevel {
  if (level !== undefined) return level;
  // A tab open across the deploy has no `level` on /api/auth/me yet.
  return role === 'admin' ? 3 : role === 'support-l2' ? 2 : role === 'support-l1' ? 1 : 0;
}

export function useAccess(): Access {
  const { user } = useAuth();
  const level = levelOf(user?.role, user?.level);
  return {
    level,
    isRubyAdmin: level === 3,
    isStaff: level >= 1,
    canEdit: (min) => level >= min,
    isUserManager: isUserManager(user),
  };
}
